import { FormattedMessage, useIntl } from 'react-intl';
import { getContextId, PageActionItem, PageActions, PageActionsMenu, PageHeader, PageTitle } from '@tlx/astro-shared';
import { AddIcon, ApprovedIcon, HistoryIcon, PopupMenuItemButton, TipsIcon, useModal } from '@tlx/atlas';
import { useTskContext } from '@Hooks/useTskContext';
import { usePageTitle } from '@Hooks/usePageTitle.ts';
import { WagePaymentsControlled } from '@Page/Overview/WagePaymentsControlled.tsx';
import { useSalaryPermission } from '@Page/Overview/hooks/useSalaryPermission.ts';

export function PageHeaderActionsOverview({
    runSalaryOverviewTour,
    year,
}: {
    runSalaryOverviewTour: () => void;
    year: number;
}) {
    const contextId = getContextId();
    const intl = useIntl();
    const pageTitle = intl.formatMessage({ id: 'tsk_salary_overview' });
    usePageTitle(pageTitle);

    const { showOldPage } = useTskContext();
    const { openerProps, modalProps } = useModal({ defaultOpen: false });
    const showWagePaymentsControlled = useSalaryPermission(year).data?.value.canAccessAnnualAccounts ?? false;

    return (
        <PageHeader>
            <PageTitle>{pageTitle}</PageTitle>
            <PageActions>
                <PageActionItem
                    id="link-sickness"
                    data-testid="link-sickness"
                    variant="secondary"
                    placement="main"
                    priority={0}
                >
                    <a href={`/execute/reimbursementMenu?contextId=${contextId}#reimbursement`}>
                        <FormattedMessage id="text_sick_payable" />
                        <AddIcon />
                    </a>
                </PageActionItem>
                <PageActionItem
                    id="link-pension"
                    data-testid="link-pension"
                    variant="secondary"
                    placement="main"
                    priority={1}
                >
                    <a href={`/execute/reimbursementMenu?contextId=${contextId}#obligatoryPension`}>
                        <FormattedMessage id="text_pension" />
                        <AddIcon />
                    </a>
                </PageActionItem>
                <PageActionItem
                    id="link-salary-payment"
                    data-testid="link-salary-payment"
                    variant="primary"
                    placement="main"
                    priority={2}
                >
                    <a href={`/page/salary/create?contextId=${contextId}`}>
                        <FormattedMessage id="text_wage_payment" />
                        <AddIcon />
                    </a>
                </PageActionItem>
                <PageActionsMenu>
                    {showOldPage && (
                        <PopupMenuItemButton
                            data-testid="oldpage-button"
                            onClick={() => {
                                window.open('/execute/wageTransactions?contextId=' + getContextId());
                            }}
                        >
                            <HistoryIcon />
                            <FormattedMessage id="text_change_to_old_page" />
                        </PopupMenuItemButton>
                    )}
                    {showWagePaymentsControlled && (
                        <PopupMenuItemButton data-testid="controlled-wage-payments--open" {...openerProps}>
                            <ApprovedIcon />
                            <FormattedMessage id="text_check_wage" />
                        </PopupMenuItemButton>
                    )}
                    <PopupMenuItemButton data-testid="run-tour-me" onClick={runSalaryOverviewTour}>
                        <TipsIcon />
                        <FormattedMessage id="text_what_is_new" />
                    </PopupMenuItemButton>
                </PageActionsMenu>
            </PageActions>
            {showWagePaymentsControlled && <WagePaymentsControlled {...modalProps} year={year} />}
        </PageHeader>
    );
}
